import React, { useState } from 'react';
import { theme } from '../../config/theme';
import { validators } from '../../utils/validators';
import { FILE_UPLOAD } from '../../config/constants';
import { formatFileSize } from '../../utils/helpers';
import { useToast } from '../common/Toast';
import Button from '../common/Button';
import { FileIcon, FolderIcon } from '../icons/Icons';

const DocumentUploadCard = ({ docInfo, onUpload, initialData, uploaded: uploadedProp }) => {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [docNumber, setDocNumber] = useState(initialData?.documentNumber || '');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(initialData?.fileUrl || null);
  const [touched, setTouched] = useState(false);
  const [isUploaded, setIsUploaded] = useState(uploadedProp || false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const fileError = validators.file(selectedFile, FILE_UPLOAD.MAX_SIZE, FILE_UPLOAD.ALLOWED_TYPES);
    if (fileError) {
      setError(fileError);
      toast.error(fileError);
      return;
    }

    setFile(selectedFile);
    setError('');

    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview('pdf');
    }
  };

  const handleDocNumberChange = (e) => {
    setDocNumber(e.target.value.toUpperCase());
    if (touched) validateDocNumber(e.target.value);
  };

  const handleDocNumberBlur = () => {
    setTouched(true);
    validateDocNumber(docNumber);
  };

  const validateDocNumber = (value) => {
    if (!docInfo.hasNumber) return;
    
    let numberError = null;
    if (docInfo.type === 'aadhaar') numberError = validators.aadhaar(value);
    else if (docInfo.type === 'pan') numberError = validators.pan(value);
    else if (docInfo.type === 'gst') numberError = validators.gst(value, docInfo.required);

    setError(numberError || '');
  };

  const handleUpload = () => {
    if (!file && !initialData) {
      setError('Please select a file');
      toast.error('Please select a file');
      return;
    }

    if (docInfo.hasNumber) {
      setTouched(true);
      validateDocNumber(docNumber);
      
      let numberError = null;
      if (docInfo.type === 'aadhaar') numberError = validators.aadhaar(docNumber);
      else if (docInfo.type === 'pan') numberError = validators.pan(docNumber);
      else if (docInfo.type === 'gst') numberError = validators.gst(docNumber, docInfo.required);

      if (numberError) {
        toast.error(numberError);
        return;
      }
    }

    onUpload({ file, docNumber, type: docInfo.type });
    setIsUploaded(true);
    toast.success(`${docInfo.label} uploaded successfully!`);
  };

  const handleReupload = () => {
    setIsUploaded(false);
    setFile(null);
    setPreview(null);
  };

  const handleSelectFile = () => {
    document.getElementById(`file-${docInfo.type}`).click();
  };

  const cardStyles = {
    border: `1px solid ${theme.colors.gray[200]}`,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    backgroundColor: theme.colors.white,
  };

  const labelStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
  };

  const uploadZoneStyles = {
    border: `2px dashed ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.xl,
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: theme.colors.gray[50],
    marginBottom: theme.spacing.md,
    transition: theme.transitions.base,
  };

  const iconWrapperStyles = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[400],
  };

  return (
    <div style={cardStyles}>
      <div style={labelStyles}>
        {docInfo.label}
        {docInfo.required && <span style={{ color: theme.colors.error }}>*</span>}
      </div>

      {preview ? (
        <div style={{ marginBottom: theme.spacing.md, textAlign: 'center' }}>
          {preview === 'pdf' ? (
            <div style={{ padding: theme.spacing.lg, backgroundColor: theme.colors.gray[50], borderRadius: theme.borderRadius.md }}>
              <div style={iconWrapperStyles}>
                <FileIcon size={48} color={theme.colors.gray[400]} />
              </div>
              <div style={{ marginTop: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                {file?.name || 'Uploaded Document'}
              </div>
            </div>
          ) : (
            <img src={preview} alt="Preview" style={{ maxWidth: '200px', borderRadius: theme.borderRadius.md }} />
          )}
          <div style={{ marginTop: theme.spacing.sm, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
            {file ? formatFileSize(file.size) : ''}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSelectFile}
            style={{ marginTop: theme.spacing.sm }}
          >
            Change File
          </Button>
        </div>
      ) : (
        <div style={uploadZoneStyles} onClick={handleSelectFile}>
          <div style={iconWrapperStyles}>
            <FolderIcon size={48} color={theme.colors.gray[400]} />
          </div>
          <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
            Click to upload
          </div>
          <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginTop: theme.spacing.xs }}>
            PDF, JPG, PNG • Max {formatFileSize(FILE_UPLOAD.MAX_SIZE)}
          </div>
        </div>
      )}

      <input
        id={`file-${docInfo.type}`}
        type="file"
        accept={FILE_UPLOAD.ALLOWED_EXTENSIONS.join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {docInfo.hasNumber && (
        <input
          type="text"
          value={docNumber}
          onChange={handleDocNumberChange}
          onBlur={handleDocNumberBlur}
          placeholder={docInfo.numberPlaceholder}
          style={{
            width: '100%',
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            border: `1px solid ${touched && error ? theme.colors.error : theme.colors.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            marginBottom: theme.spacing.md,
            fontSize: theme.typography.fontSize.sm,
          }}
        />
      )}

      {touched && error && (
        <div style={{ color: theme.colors.error, fontSize: theme.typography.fontSize.xs, marginBottom: theme.spacing.md }}>
          {error}
        </div>
      )}

      {/* Success state after upload */}
      {isUploaded && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing.md,
          backgroundColor: `${theme.colors.success}15`,
          border: `1px solid ${theme.colors.success}`,
          borderRadius: theme.borderRadius.md,
          marginBottom: theme.spacing.md,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, color: theme.colors.success, fontWeight: theme.typography.fontWeight.medium, fontSize: theme.typography.fontSize.sm }}>
            <span style={{ fontSize: '18px' }}>✓</span>
            Uploaded successfully — ready for submission
          </div>
          <Button variant="secondary" size="sm" onClick={handleReupload}>Re-upload</Button>
        </div>
      )}

      {!isUploaded && file && (
        <Button onClick={handleUpload} fullWidth>
          Upload {docInfo.label}
        </Button>
      )}
    </div>
  );
};

export default DocumentUploadCard;